<script setup lang="ts">
import APIActivityHeatmap from '@/components/APIActivityHeatmap.vue'
import { apiErrorWrapper, clarityClient } from '@/lib/clarity_client'
import { getLocalTimeZone, today } from '@internationalized/date'
import { useHead } from '@unhead/vue'
import { useAsyncState } from '@vueuse/core'

useHead({ title: 'Activity' })

const { state, isLoading, error } = useAsyncState(
  async () => {
    const tz = getLocalTimeZone()
    const now = today(tz)
    const startDate = now.subtract({ weeks: 52 })

    const { data, response } = await clarityClient.GET('/v1/api/activity', {
      params: { query: { timezone: tz, start: startDate.toString() } },
    })

    if (!data) {
      throw apiErrorWrapper('No data returned', response)
    }

    return data
  },
  undefined,
  { immediate: true },
)
</script>

<template>
  <div class="flex flex-col container">
    <div class="flex flex-col items-center pb-12">
      <div class="grid grid-cols-1 gap-8 w-full pt-4 px-2">
        <div>
          <div class="flex flex-col gap-4">
            <div class="flex small-card-header items-center gap-2">
              <div class="flex-1">
                <h2>Activity Overview</h2>
              </div>
            </div>
            <div class="mx-auto max-w-4xl">
              This is a display of ClarityBoss activity data from your account, as well as activity data from any
              accounts that are shared with you. We are looking to make this more robust and useful over time. If you
              have any feedback or suggestions on what you'd like to see here, please let us know!
            </div>
            <div v-if="error" class="text-red-500 p-2">Failed to load activity data. Please try again.</div>
            <div v-else-if="isLoading" class="text-muted-foreground p-2">Loading activity data...</div>
            <div v-if="state" class="space-y-8">
              <div>
                <h3 class="text-xl font-bold">{{ state.self.overall.name }} (You)</h3>
                <APIActivityHeatmap :activity-data="state.self.overall" :weeks-to-show="52" />

                <div class="grid md:grid-cols-2 xl:grid-cols-3 ml-4">
                  <div v-for="(person, personId) in state.self.byPerson" :key="personId" class="mt-6">
                    <h3 class="text-lg font-semibold">{{ person.name }}</h3>
                    <APIActivityHeatmap :activity-data="person" :person-only="true" :weeks-to-show="13" />
                  </div>
                </div>
                <div v-if="Object.keys(state.self.byPerson).length === 0" class="text-muted-foreground mt-4 p-2">
                  No direct reports found.
                </div>
              </div>

              <div v-for="shared in state.shared" :key="shared.overall.name">
                <h3 class="text-xl font-bold">{{ shared.overall.name }}</h3>
                <APIActivityHeatmap :activity-data="shared.overall" :weeks-to-show="52" />

                <div class="grid md:grid-cols-2 xl:grid-cols-3 ml-4">
                  <div v-for="(person, personId) in shared.byPerson" :key="personId" class="mt-6">
                    <h3 class="text-lg font-semibold">{{ person.name }}</h3>
                    <APIActivityHeatmap :activity-data="person" :person-only="true" :weeks-to-show="13" />
                  </div>
                </div>
                <div v-if="Object.keys(shared.byPerson).length === 0" class="text-muted-foreground mt-4 p-2">
                  No direct reports found, or you do not have permission to view them.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
